import type { Control, UseFormSetValue } from 'react-hook-form';

import type { ReceiveConfirmLineDto } from '@/entities/receive';

import { getFieldCeiling, getRemainingQty, redistributeQuantities } from '../lib/quantities';
import { useConfirmArrivalLine } from '../model/use-confirm-arrival-line';
import {
  getConfirmLineKey,
  type ConfirmArrivalFormValues,
  type ConfirmArrivalQuantityField,
  type ConfirmArrivalUnitMode
} from '../model/types';
import { ConfirmArrivalQuantityCell } from './confirm-arrival-quantity-cell';
import { ConfirmArrivalQuantityFigure } from './confirm-arrival-quantity-figure';

interface QuantityCellProps {
  control: Control<ConfirmArrivalFormValues>;
  setValue: UseFormSetValue<ConfirmArrivalFormValues>;
  line: ReceiveConfirmLineDto;
  index: number;
  field: ConfirmArrivalQuantityField;
  label: string;
  unitMode: ConfirmArrivalUnitMode;
  secondaryOpen: boolean;
  onToggleSecondary: () => void;
}

/** Good / Damaged / Lost of one line; an edit rebalances the three buckets together. */
export function ConfirmArrivalLineQuantityCell({
  control,
  setValue,
  line,
  index,
  field,
  label,
  unitMode,
  secondaryOpen,
  onToggleSecondary
}: QuantityCellProps) {
  const { name, values, errors } = useConfirmArrivalLine(control, index);
  if (!values) return null;

  return (
    <ConfirmArrivalQuantityCell
      id={`${getConfirmLineKey(line)}-${field}`}
      label={`${line.lineReference} — ${label}`}
      line={line}
      unitMode={unitMode}
      value={values[field]}
      ceiling={getFieldCeiling(line, field)}
      // One write per edit: the three buckets are rebalanced as a whole row.
      onChange={(inventoryQty) =>
        setValue(name, redistributeQuantities(line, values, field, inventoryQty), {
          shouldDirty: true,
          shouldValidate: true
        })
      }
      secondaryOpen={secondaryOpen}
      onToggleSecondary={onToggleSecondary}
      error={errors?.[field]?.message}
    />
  );
}

interface RemainingCellProps {
  control: Control<ConfirmArrivalFormValues>;
  line: ReceiveConfirmLineDto;
  index: number;
  unitMode: ConfirmArrivalUnitMode;
}

/** What is still outstanding on the line once the entry is subtracted. */
export function ConfirmArrivalLineRemainingCell({ control, line, index, unitMode }: RemainingCellProps) {
  const { values } = useConfirmArrivalLine(control, index);
  if (!values) return null;

  return (
    <ConfirmArrivalQuantityFigure
      line={line}
      unitMode={unitMode}
      quantity={getRemainingQty(line, values)}
      kind="remaining"
    />
  );
}
